import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { TranslateOutlined } from '@mui/icons-material';
import { FbeForm, FbeFrontline, getLanguageDisplayName, LocalisedText } from '../../services/fbeTypes';
import { getLocalisedText, hasMeaningfulText, upsertLocalisedText } from '../../services/translationCompletion';
import RichTextField from '../RichTextField';

interface FormTranslationsTabProps {
  formState: FbeForm;
  supportedLanguages: string[];
  frontline: FbeFrontline | null;
  onSave: (updatedForm: FbeForm) => void;
  readOnly?: boolean;
  isAdmin?: boolean;
  selectedLanguage?: string;
  onDraftChange?: (lang: string, name: string, description: string) => void;
  onDirtyChange?: (lang: string, name: string, description: string) => void;
  onDefaultLanguageChange?: (lang: string) => void;
  /** Pre-populated draft values to restore after a conflict-blocked reload */
  initialDraftName?: LocalisedText[];
  initialDraftDescriptions?: LocalisedText[];
}

const SideBySideRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px',
  alignItems: 'flex-start',
});

const ColumnHeader = styled(Typography)({
  fontWeight: 600,
  fontSize: '13px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#666',
  marginBottom: '8px',
});

const SourceBox = styled(Box)({
  backgroundColor: '#f5f5f5',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  padding: '12px',
  minHeight: '56px',
  fontSize: '14px',
  color: '#444',
  lineHeight: 1.5,
  wordBreak: 'break-word',
});

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '14px',
  color: '#333',
  marginTop: '20px',
  marginBottom: '12px',
  borderBottom: '1px solid #e0e0e0',
  paddingBottom: '8px',
});

export default function FormTranslationsTab({
  formState,
  supportedLanguages,
  frontline,
  onSave,
  readOnly = false,
  selectedLanguage,
  onDraftChange,
  onDirtyChange,
  initialDraftName,
  initialDraftDescriptions,
}: FormTranslationsTabProps): JSX.Element {
  const defaultLang = formState.defaultLanguage || (formState.name && formState.name[0] && formState.name[0].language) || 'en';
  const currentDefaultLang = defaultLang;
  // The General Info tab's `description` field is the live source of truth for the
  // default language — it must win over any (possibly stale) entry already in `descriptions`.
  const mergeDefaultLangDescription = useCallback(
    (descriptions: LocalisedText[] | undefined): LocalisedText[] => {
      const others = (descriptions || []).filter((t) => t.language !== defaultLang);
      // Treat an explicit empty string as an intentional value — only fall
      // back to descriptions when `description` is undefined.
      if (formState.description !== undefined) {
        return [{ language: defaultLang, text: formState.description }, ...others];
      }
      const existing = (descriptions || []).find((t) => t.language === defaultLang);
      return existing ? [existing, ...others] : others;
    },
    [defaultLang, formState.description],
  );
  const fallbackDescriptions = useMemo(
    () => mergeDefaultLangDescription(formState.descriptions),
    [formState.descriptions, mergeDefaultLangDescription],
  );
  const [fromLang, setFromLang] = useState<string>(defaultLang);
  // Use selectedLanguage as the initial target when coming from the translations submenu
  const [toLang, setToLang] = useState<string>(
    (selectedLanguage && selectedLanguage !== defaultLang)
      ? selectedLanguage
      : supportedLanguages.find((l) => l !== defaultLang) || '',
  );

  useEffect(() => {
    if (selectedLanguage && selectedLanguage !== toLang && selectedLanguage !== fromLang) {
      setToLang(selectedLanguage);
    }

  }, [selectedLanguage, fromLang]);

  // ── Form-level drafts ──────────────────────────────────────────────────────
  const [draftName, setDraftName] = useState<LocalisedText[]>(initialDraftName ?? formState.name);
  const [draftDescriptions, setDraftDescriptions] = useState<LocalisedText[]>(
    initialDraftDescriptions ?? fallbackDescriptions
  );
  const restoredDraftHydratedRef = useRef(false);
  const targetLanguages = useMemo(
    () => supportedLanguages.filter((lang) => lang !== fromLang),
    [fromLang, supportedLanguages],
  );

  useEffect(() => {
    const hasRestoredName = initialDraftName !== undefined;
    const hasRestoredDescriptions = initialDraftDescriptions !== undefined;

    if (restoredDraftHydratedRef.current || (!hasRestoredName && !hasRestoredDescriptions)) {
      return;
    }

    setDraftName(initialDraftName ?? formState.name);
    setDraftDescriptions(initialDraftDescriptions ?? fallbackDescriptions);
    restoredDraftHydratedRef.current = true;
  }, [fallbackDescriptions, formState.name, initialDraftDescriptions, initialDraftName]);


  const langLabel = (code: string) =>
    getLanguageDisplayName(code, frontline?.settings.languageDisplayNames || {});

  // ── Completion ─────────────────────────────────────────────────────────────
  const draftCompletion = useMemo(() => {
    const fields = [
      getLocalisedText(draftName, toLang),
      getLocalisedText(draftDescriptions, toLang),
    ];
    const filled = fields.filter(hasMeaningfulText).length;
    return fields.length > 0 ? Math.round((filled / fields.length) * 100) : 0;
  }, [draftName, draftDescriptions, toLang]);

  // ── Language selectors ─────────────────────────────────────────────────────
  const handleFromChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const newFrom = e.target.value;
      setFromLang(newFrom);
      if (toLang === newFrom) {
        const next = supportedLanguages.find((l) => l !== newFrom);
        if (next) setToLang(next);
      }
    },
    [toLang, supportedLanguages],
  );
  const handleToChange = useCallback((e: SelectChangeEvent<string>) => {
    setToLang(e.target.value);
  }, []);

  // ── Form-level handlers ────────────────────────────────────────────────────
  // Notify parent of dirty state immediately (not debounced) so unsaved-changes
  // guard is never bypassed when the user switches tabs quickly.
  const notifyDirtyChange = useCallback((lang: string, name: string, description: string) => {
    if (onDirtyChange) onDirtyChange(lang, name, description);
  }, [onDirtyChange]);

  // Debounce onDraftChange so EditForm (a large component) doesn't re-render
  // on every single keystroke — only ~300 ms after the user stops typing.
  const draftChangeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyDraftChange = useCallback((lang: string, name: string, description: string) => {
    if (!onDraftChange) return;
    if (draftChangeTimer.current) clearTimeout(draftChangeTimer.current);
    draftChangeTimer.current = setTimeout(() => {
      onDraftChange(lang, name, description);
    }, 300);
  }, [onDraftChange]);

  const handleNameChange = useCallback(
    (value: string) => {
      setDraftName((prev) => upsertLocalisedText(prev, toLang, value));
      notifyDirtyChange(toLang, value, getLocalisedText(draftDescriptions, toLang));
      notifyDraftChange(toLang, value, getLocalisedText(draftDescriptions, toLang));
    },
    [toLang, notifyDirtyChange, notifyDraftChange, draftDescriptions],
  );
  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDraftDescriptions((prev) => upsertLocalisedText(prev, toLang, value));
      notifyDirtyChange(toLang, getLocalisedText(draftName, toLang), value);
      notifyDraftChange(toLang, getLocalisedText(draftName, toLang), value);
    },
    [toLang, notifyDirtyChange, notifyDraftChange, draftName],
  );

  // Notify parent when the target language changes (not debounced — instant)
  useEffect(() => {
    if (onDraftChange) {
      onDraftChange(toLang, getLocalisedText(draftName, toLang), getLocalisedText(draftDescriptions, toLang));
    }

  }, [toLang]);

  // ── Save ───────────────────────────────────────────────────────────────────  // Dirty: true when draft name or descriptions differ from the saved formState
  const savedDescriptions = useMemo(
    () => mergeDefaultLangDescription(formState.descriptions),
    [formState.descriptions, mergeDefaultLangDescription],
  );
  const isDirty = useMemo(
    () => {
      const sortByLang = (arr: LocalisedText[]) => [...arr].sort((a, b) => a.language.localeCompare(b.language));
      return (
        JSON.stringify(sortByLang(draftName)) !== JSON.stringify(sortByLang(formState.name)) ||
        JSON.stringify(sortByLang(draftDescriptions)) !== JSON.stringify(sortByLang(savedDescriptions))
      );
    },
    [draftName, draftDescriptions, formState.name, savedDescriptions],
  );
  const handleSave = () => {
    const sourceNameEntry = formState.name.find((t) => t.language === fromLang);
    let mergedName = draftName.filter((t) => t.language !== fromLang);
    if (sourceNameEntry) mergedName = [sourceNameEntry, ...mergedName];

    let mergedDescs = draftDescriptions.filter((t) => t.language !== fromLang);
    if (fromLang === defaultLang && formState.description) {
      mergedDescs = [{ language: fromLang, text: formState.description }, ...mergedDescs];
    } else {
      const sourceDescEntry = (formState.descriptions || []).find((t) => t.language === fromLang);
      if (sourceDescEntry) mergedDescs = [sourceDescEntry, ...mergedDescs];
    }

    onSave({ ...formState, name: mergedName, descriptions: mergedDescs });
  };

  // ── Default language mismatch detection ────────────────────────────────────
  // Warn when the default language has no content in the form name but another
  // language does — this silently breaks completion % and the untranslated filter.
  const defaultLangHasContent = hasMeaningfulText(getLocalisedText(formState.name, currentDefaultLang));
  const firstOtherLangWithContent = !defaultLangHasContent
    ? formState.name.find((t) => hasMeaningfulText(t.text) && t.language !== currentDefaultLang) ?? null
    : null;
  const defaultLangMismatch = !defaultLangHasContent && firstOtherLangWithContent !== null;

  // ── Source / target text helpers ───────────────────────────────────────────
  const sourceName = getLocalisedText(formState.name, fromLang);
  const sourceDescription = fromLang === defaultLang
    ? (formState.description !== undefined ? formState.description : (getLocalisedText(formState.descriptions || [], fromLang) || ''))
    : getLocalisedText(formState.descriptions || [], fromLang);
  const targetName = getLocalisedText(draftName, toLang);
  const targetDescription = getLocalisedText(draftDescriptions, toLang);

  return (
    <Box sx={{ padding: '24px' }}>


      {defaultLangMismatch && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The form content appears to be written in{' '}
          <strong>{langLabel(firstOtherLangWithContent!.language)}</strong>, but the default
          language is set to <strong>{langLabel(currentDefaultLang)}</strong>. Translation
          completion percentages and the "Only untranslated" filter will not work correctly
          until this is fixed.{' '}
          {'Update the default language in the form\'s General Info tab (Edit → General Info).'}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ── Translation session selectors + badge ── */}
      <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ fontSize: '12px', mb: 0.5 }}>From (source)</FormLabel>
          <Select value={fromLang} onChange={handleFromChange} inputProps={{ 'aria-label': 'Source language' }}>
            {supportedLanguages.map((lang) => (
              <MenuItem key={lang} value={lang}>{langLabel(lang)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <FormLabel sx={{ fontSize: '12px', mb: 0.5 }}>To (target)</FormLabel>
          <Select
            value={toLang}
            onChange={handleToChange}
            displayEmpty
            disabled={targetLanguages.length === 0}
            inputProps={{ 'aria-label': 'Target language' }}
          >
            <MenuItem value="" disabled>
              <em style={{ color: '#aaa' }}>Select language</em>
            </MenuItem>
            {/* Inject the current target lang as an option if the filtered list hasn't loaded it yet */}
            {toLang && !targetLanguages.includes(toLang) && (
              <MenuItem value={toLang}>{langLabel(toLang)}</MenuItem>
            )}
            {targetLanguages.map((lang) => (
              <MenuItem key={lang} value={lang}>{langLabel(lang)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={2.5}>
          <Chip
            icon={<TranslateOutlined fontSize="small" />}
            label={`${draftCompletion}% translated`}
            color={draftCompletion === 100 ? 'success' : draftCompletion > 0 ? 'warning' : 'default'}
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      {/* ── Column headers ── */}
      <SideBySideRow>
        <ColumnHeader>{langLabel(fromLang)} (source)</ColumnHeader>
        <ColumnHeader>{langLabel(toLang)} (target)</ColumnHeader>
      </SideBySideRow>

      {/* ── Form Name ── */}
      <SectionTitle>Form Name</SectionTitle>
      <SideBySideRow>
        <SourceBox sx={{ minHeight: '56px' }}>
          {sourceName || <em style={{ color: '#aaa' }}>No text in source language</em>}
        </SourceBox>
        <TextField
          id={`translation-name-${toLang}`}
          size="small"
          fullWidth
          value={targetName}
          onChange={(e) => handleNameChange(e.target.value)}
          disabled={readOnly}
          sx={{ backgroundColor: 'white' }}
          inputProps={{ 'aria-label': 'Translated form name' }}
        />
      </SideBySideRow>

      {/* ── Form Description ── */}
      <SectionTitle>Form Description</SectionTitle>
      <SideBySideRow sx={{ alignItems: 'stretch' }}>
        {sourceDescription ? (
          <SourceBox dangerouslySetInnerHTML={{ __html: sourceDescription }} sx={{ '& p': { margin: '4px 0' } }} />
        ) : (
          <SourceBox><em style={{ color: '#aaa' }}>No description in source language</em></SourceBox>
        )}
        <Box>
          <RichTextField
            labelText="" labelId={`translation-description-${toLang}`}
            mandatory={false} helpTextTitle="" helpTextBody=""
            onChange={handleDescriptionChange} value={targetDescription}
            disabled={readOnly} error={false} helperText=""
            onLock={() => {}} disabledLock={true} displayedLock={false}
          />
        </Box>
      </SideBySideRow>

      {/* ── Save ── */}
      {!readOnly && (
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained" color="primary" onClick={handleSave}
            disabled={!isDirty}
            sx={{ borderRadius: '24px', padding: '6px 50px' }}
          >
            Save Translations
          </Button>
        </Box>
      )}
    </Box>
  );
}